<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Popover from 'primevue/popover'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import { useNotifications } from '@/composables/useNotifications'

const router = useRouter()
const notificationPanel = ref()
const listContainer = ref()
const markingAllRead = ref(false)

const {
  notifications,
  unreadCount,
  loading,
  hasMore,
  loadNotifications,
  loadMore,
  markAsRead: markAsReadApi,
  markAllAsRead: markAllReadApi
} = useNotifications()

const toggle = (event) => {
  notificationPanel.value.toggle(event)
}

const markAsRead = async (id) => {
  // Помечаем как прочитанное и удаляем из списка
  await markAsReadApi(id)
}

const markAllAsRead = async () => {
  markingAllRead.value = true
  try {
    await markAllReadApi()
  } finally {
    markingAllRead.value = false
  }
}

const handleNotificationClick = async (notification) => {
  // Отметить как прочитанное и удалить из списка
  if (!notification.is_read) {
    await markAsRead(notification.id)
  }

  // Навигация в зависимости от типа
  if (notification.notification_type === 'MESSAGE_RECEIVED' && notification.metadata?.conversation_id) {
    await router.push(`/messages/${notification.metadata.conversation_id}`)
    notificationPanel.value.hide()
  } else if (notification.notification_type === 'TASK_CREATED' && notification.entity_id) {
    await router.push(`/tasks/${notification.entity_id}`)
    notificationPanel.value.hide()
  } else if (notification.notification_type === 'TASK_ASSIGNED' && notification.entity_id) {
    await router.push(`/tasks/${notification.entity_id}`)
    notificationPanel.value.hide()
  } else if (notification.notification_type === 'RESPONSIBILITY_ASSIGNED' && notification.entity_id) {
    await router.push(`/tasks/${notification.entity_id}`)
    notificationPanel.value.hide()
  }
}

const getNotificationIcon = (type) => {
  const icons = {
    MESSAGE_RECEIVED: 'pi pi-envelope',
    TASK_CREATED: 'pi pi-list',
    TASK_ASSIGNED: 'pi pi-user',
    RESPONSIBILITY_ASSIGNED: 'pi pi-user-edit',
    WORK_ASSIGNED: 'pi pi-briefcase',
    COMMENT_MENTIONED: 'pi pi-at'
  }
  return icons[type] || 'pi pi-bell'
}

const formatTime = (timestamp) => {
  try {
    return new Date(timestamp).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return timestamp
  }
}

const onScroll = () => {
  const container = listContainer.value
  if (!container || loading.value || !hasMore.value) return

  const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight
  if (scrollBottom < 100) {
    loadMore()
  }
}

onMounted(() => {
  loadNotifications()
})

defineExpose({
  toggle
})
</script>
<template>
  <div class="notification-panel">
    <Popover ref="notificationPanel" style="width: 400px; max-width: 90vw;">
      <div class="notification-header">
        <h3>Уведомления</h3>
        <Button
          v-if="unreadCount > 0"
          label="Отметить все"
          text
          size="small"
          @click="markAllAsRead"
          :loading="markingAllRead"
        />
      </div>

      <div class="notification-list" @scroll="onScroll" ref="listContainer">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 'notification-unread': !notification.is_read }"
          @click="handleNotificationClick(notification)"
        >
          <div class="notification-icon">
            <i :class="getNotificationIcon(notification.notification_type)" />
          </div>
          <div class="notification-content">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-text">{{ notification.text }}</div>
            <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
          </div>
          <div class="notification-actions">
            <Button
              v-if="!notification.is_read"
              icon="pi pi-check"
              text
              rounded
              size="small"
              @click.stop="markAsRead(notification.id)"
            />
          </div>
        </div>

        <div v-if="loading" class="notification-loading">
          <ProgressSpinner style="width: 30px; height: 30px" />
        </div>

        <div v-if="!loading && notifications.length === 0" class="notification-empty">
          <i class="pi pi-bell" style="font-size: 3rem; color: #ccc;" />
          <p>Нет уведомлений</p>
        </div>
      </div>
    </Popover>
  </div>
</template>



<style scoped>
.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #e0e0e0;
}

.notification-header h3 {
  margin: 0;
  font-size: 1rem;
}

.notification-filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.notification-list {
  max-height: 500px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #f9f9f9;
}

.notification-unread {
  background-color: #f0f7ff;
}

.notification-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e3f2fd;
  border-radius: 50%;
  color: #1976d2;
}

.notification-content {
  flex-grow: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.notification-text {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-time {
  font-size: 0.8rem;
  color: #999;
}

.notification-actions {
  flex-shrink: 0;
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.notification-loading {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #999;
}

.notification-empty p {
  margin-top: 1rem;
  font-size: 1rem;
}
</style>
